<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { mobile } from 'src/services/mobile-rework';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { profile_data } from 'src/stores/account/user';
	import { navigateToShop } from '../Lists/list-helpers';
	import { ButtonV2, Container, Icon, Modal, Text } from '@Shelly/shelly-design-system';
	import { push } from 'svelte-spa-router';
	import { copy } from 'src/helpers/copy_text';
	import { dateFormat } from 'src/helpers/date_helper';
	import type { DiscountDetailsType } from 'src/types/DiscountType';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let showModal = false;
	export let g1dItems: DiscountDetailsType[] = [];

	const dispatch = createEventDispatcher();

	const onCloseG1ReplacementModal = () => {
		if (showModal) {
			showModal = false;
			if (g1dItems.length > 0) {
				const g1d = { ...$profile_data.aep.g1d };
				g1dItems.forEach(({ key, ...item }) => {
					if (!g1d[key]) {
						return;
					}
					g1d[key] = {
						...g1d[key],
						...item,
						seen: Math.floor(Date.now() / 1000),
					};
				});
				save_profile('aep', { ...$profile_data.aep, g1d });
			}
			dispatch('close');
		}
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', onCloseG1ReplacementModal);
			return () => mobile.removeEventListener('back_button', onCloseG1ReplacementModal);
		}
	});

	let expiry = dateFormat(new Date('2026-04-30'), 'd M Y');
</script>

<Modal hideTitle {showModal} on:close={onCloseG1ReplacementModal}>
	<Container slot="content" padding="0">
		<div class="d-flex flex-direction-column align-items-center text-center">
			<Text styleType="h1" class="mb-m">
				{@html $format('new_title_gen1_replacement', {
					default: 'Gen1 Upgrade Programme',
				})}
			</Text>
			<Text class="mb-s text-300">
				{$format('new_text_gen1_replacement_1', {
					default:
						'Thank you for using Shelly devices and for supporting us from the very beginning.',
				})}
			</Text>
			<Text class="mb-s text-300">
				{@html $format('new_text_gen1_replacement_2', {
					default:
						'Your <span class="text-bold">Gen1 devices</span> played an important role in building the Shelly ecosystem as it is today — and we truly appreciate your trust.',
				})}
			</Text>
			<Text class="mb-s text-300">
				{@html $format('new_text_gen1_replacement_3', {
					default:
						"To say thank you, we're offering you a <span class='text-bold'>special upgrade opportunity</span>.",
				})}
			</Text>
			<Text class="mb-m text-300">
				{@html $format('new_text_gen1_replacement_4', {
					default:
						"As a Gen1 user, you can upgrade your <span class='text-bold'>Shelly 1, Shelly 1PM, or Shelly 2PM</span> to <span class='text-bold'>new, more powerful Shelly Gen3 devices</span> under an exclusive <span class='text-bold'></span>Gen1 Upgrade Programme</span>.",
				})}
			</Text>
			<div
				class="discount-code-container d-flex flex-direction-column align-items-center justify-content-center mb-m"
			>
				<Text styleType="h1" class="text-center mb-xs text-600">
					{$format('label_discount_code', { default: 'Discount code:' })}
				</Text>
				<div class="d-flex align-items-center justify-content-center">
					<Text styleType="h1" class="mr-xs text-center text-normal">
						{g1dItems?.[0]?.code}
					</Text>
					<Icon
						class="cursor-pointer"
						icon="far fa-copy"
						on:click={() => {
							copy(
								g1dItems?.[0]?.code,
								$format('discount_code_copied_successfully', {
									default: 'Discount code copied successfully!',
								}),
							);
						}}
					/>
				</div>
			</div>
			<Text class="mb-m text-300">
				{@html $format('new_text_gen1_replacement_5', {
					default: `Use your personal discount code above to get <span class="text-bold">50% off on Shelly Gen3 devices</span> in the Shelly Store. No device return required. Just an easy upgrade to faster hardware, more features, and longer future support. Offer is valid until <span class="text-bold">{expiry}</span>.`,
					values: { expiry },
				})}
			</Text>
			<div class="bottom-content d-flex width-full mb-l">
				<ButtonV2
					class="width-full text-nowrap mr-xs"
					variant="primary"
					on:click={() => {
						onCloseG1ReplacementModal();
						navigateToShop('https://www.shelly.com/collections/all-products/gen3r');
					}}
				>
					{$format('button_shop_now', { default: 'Shop Now' })}
				</ButtonV2>
				<ButtonV2
					class="width-full text-nowrap ml-xs"
					variant="secondary"
					on:click={() => {
						showModal = false;
						push($showNewLayout ? '/explore-shelly/discounts' : '/settings/discounts');
					}}
				>
					{$format('button_active_discounts', {
						default: 'Active Discounts',
					})}
				</ButtonV2>
			</div>
			<Text styleType="body-small" class="text-center text-color-muted text-300">
				{@html $format('new_text_gen1_replacement_6', {
					default:
						'Please see the <a class="link-styles text-small text-600" href="https://www.shelly.com/pages/gen1-replacement-info" target="_blank">FAQ</a> for terms and conditions.',
				})}
			</Text>
		</div>
	</Container>
</Modal>

<style>
	.discount-code-container {
		background-image: url('/images/discount_active_bg.png');
		background-repeat: no-repeat;
		background-position: center;
		background-size: contain;
		height: 161px;
		width: 300px;
	}

	.bottom-content {
		width: 100%;
		max-width: 480px;
		box-sizing: border-box;
	}
</style>
